<template>
  <Modal
    :visible="open"
    :title="modalTitle"
    width="520px"
    :footer="null"
    :mask-closable="false"
    @cancel="handleCancel"
  >
    <div class="wallet-pay-amount">
      应付金额：<span class="amount">{{ formatMoney(amount) }}</span>
    </div>
    <div class="wallet-pay-route" v-if="routeHint">{{ routeHint }}</div>

    <div class="wallet-section-title">支付方式</div>
    <div class="wallet-pay-types">
      <div
        class="pay-type-card"
        :class="{ active: paymentMethod === 1 }"
        @click="paymentMethod = 1"
      >
        <div class="pay-type-name">授信额度</div>
        <div class="pay-type-balance">可用：￥{{ formatMoney(walletInfo.creditAvailable) }}</div>
      </div>
      <div
        class="pay-type-card"
        :class="{ active: paymentMethod === 2 }"
        @click="paymentMethod = 2"
      >
        <div class="pay-type-name">预存额度</div>
        <div class="pay-type-balance">可用：￥{{ formatMoney(walletInfo.reservedAvailable) }}</div>
      </div>
    </div>

    <div class="wallet-pay-actions">
      <Button @click="handleCancel">取消</Button>
      <Button type="primary" :loading="submitting" @click="openPasswordModal">
        确认支付
      </Button>
    </div>
  </Modal>

  <Modal
    :visible="passwordOpen"
    title="支付验证"
    width="400px"
    :confirm-loading="submitting"
    ok-text="确认支付"
    cancel-text="取消"
    @ok="handleConfirmPay"
    @cancel="passwordOpen = false"
  >
    <InputPassword
      v-model:value="password"
      placeholder="请输入支付密码"
      maxlength="10"
      autocomplete="new-password"
      @pressEnter="handleConfirmPay"
    />
  </Modal>
</template>

<script setup>
import { Button, Input, Modal, message } from 'ant-design-vue'
import { reactive, ref, watch } from 'vue'
import { fetchWalletInfo, fetchWalletPay, fetchWalletReschedulePay } from '@/service'

const InputPassword = Input.Password

const props = defineProps({
  open: { type: Boolean, default: false },
  amount: { type: [Number, String], default: 0 },
  orderSn: { type: String, default: '' },
  outOrderSn: { type: String, default: '' },
  routeHint: { type: String, default: '' },
  modalTitle: { type: String, default: '支付出票' },
  paySubject: { type: String, default: '火车票出票' },
  /** issue | reschedule */
  payType: { type: String, default: 'issue' },
  /** 金额为0时仍允许支付（改签平改验密） */
  allowZeroAmount: { type: Boolean, default: false }
})

const emit = defineEmits(['update:open', 'success', 'cancel'])

const walletInfo = reactive({
  creditAvailable: 0,
  reservedAvailable: 0
})
const paymentMethod = ref(0)
const passwordOpen = ref(false)
const password = ref('')
const submitting = ref(false)

const formatMoney = (val) => {
  const num = Number(val)
  if (Number.isNaN(num)) return '0.00'
  return num.toFixed(2)
}

const loadWalletInfo = () => {
  fetchWalletInfo()
    .then((res) => {
      if (res?.success && res.data) {
        walletInfo.creditAvailable = res.data.creditAvailable ?? 0
        walletInfo.reservedAvailable = res.data.reservedAvailable ?? 0
      }
    })
    .catch(() => {
      message.error('获取钱包余额失败')
    })
}

watch(
  () => props.open,
  (visible) => {
    if (visible) {
      paymentMethod.value = 0
      password.value = ''
      passwordOpen.value = false
      loadWalletInfo()
    }
  }
)

const handleCancel = () => {
  emit('update:open', false)
  emit('cancel')
}

const openPasswordModal = () => {
  const payAmount = Number(props.amount)
  if (!paymentMethod.value) {
    return message.warning('请选择授信或预存支付方式')
  }
  const available =
    paymentMethod.value === 1
      ? Number(walletInfo.creditAvailable)
      : Number(walletInfo.reservedAvailable)
  if (payAmount > 0 && available < payAmount) {
    return message.warning(
      paymentMethod.value === 1 ? '授信额度不足' : '预存额度不足'
    )
  }
  if (payAmount <= 0 && !props.allowZeroAmount) {
    return message.warning('支付金额不正确')
  }
  passwordOpen.value = true
}

const handleConfirmPay = () => {
  if (!password.value) {
    return message.warning('请输入支付密码')
  }
  if (submitting.value) return
  submitting.value = true
  const payload = {
    orderSn: props.orderSn,
    outOrderSn: props.outOrderSn || undefined,
    totalAmount: Number(props.amount),
    paymentMethod: paymentMethod.value,
    password: password.value,
    subject: props.paySubject || props.routeHint || '火车票出票'
  }
  const payRequest =
    props.payType === 'reschedule' ? fetchWalletReschedulePay : fetchWalletPay
  payRequest(payload)
    .then((res) => {
      if (res?.success) {
        passwordOpen.value = false
        emit('update:open', false)
        emit('success', res.data)
      } else {
        message.error(res?.message || '支付失败')
      }
    })
    .catch(() => {
      message.error('支付异常，请稍后重试')
    })
    .finally(() => {
      submitting.value = false
    })
}
</script>

<style lang="scss" scoped>
.wallet-pay-amount {
  text-align: center;
  margin-bottom: 8px;
  font-size: 15px;
  .amount {
    color: #dc2408;
    font-size: 28px;
    font-weight: 700;
  }
}
.wallet-pay-route {
  text-align: center;
  color: #666;
  margin-bottom: 16px;
  font-size: 13px;
}
.wallet-section-title {
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}
.wallet-pay-types {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.pay-type-card {
  flex: 1;
  border: 1px solid #d9e2f2;
  border-radius: 10px;
  padding: 14px 12px;
  cursor: pointer;
  background: #f7f9fc;
  transition: all 0.2s;
  &.active {
    border-color: #ff8001;
    background: #fff7ed;
    box-shadow: 0 0 0 1px #ff8001 inset;
  }
}
.pay-type-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}
.pay-type-balance {
  font-size: 13px;
  color: #666;
}
.wallet-pay-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
